import React from "react";
import {
    useGridApiRef,
    DataGridPro,
    GridRowId,
    GridColumnVisibilityModel,
    GridFilterModel,
} from "@mui/x-data-grid-pro";
import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import {
    useRemoveUserPreferenceMutation,
    useSetUserPreferenceMutation,
} from "../../../services/cclTokenedUserPreferenceApi";
import CclDataGridToolbar from "../cclDataGridToolbar/cclDataGridToolbar";
import { AlertColor } from "@mui/material";
import CclAlertSnackbar from "../cclAlertSnackbar";

export enum ePersistentDataGridStateChangeResult {
    SaveSuccess,
    SaveFailure,
    ClearSuccess,
    ClearFailure,
}

export interface CclPersistentDataGridProps {
    columns: any[];
    data: any[];
    loading: boolean;
    stateSelector: any;
    userPreferenceId: number;
    getRowIdFn: any;
    actionCreator: ActionCreatorWithPayload<any, any>;
    hideToolbar?: boolean;
    initState?: string;
    resultBar?: boolean;
    resultBarText?: string;
    miscButtons?: CclPersistantDataGridMiscButton[];
    showCheckboxes?: boolean;
    disableSelectionOnClick?: boolean;
    hideSettingsButtons?: boolean;
    columnVisibility?: GridColumnVisibilityModel;
    exportName?: string;
    onSelectedRowsChange?: (rows?: any[]) => any;
    hideFooter?: boolean;
    autoHeight?: boolean;
    selectionModel?: any;
    filterModel?: GridFilterModel;
    onTableStateChange?: (result: ePersistentDataGridStateChangeResult) => void;
}

export interface CclPersistantDataGridMiscButton {
    id: string;
    caption: string;
    onClick: (rows?: any[]) => any;
    onToggled?: (checked: boolean) => any;
    type?: string;
    checkedState?: boolean; // For checkboxes, otherwise unneeded
    restricted: boolean;
    disable?: boolean;
    returnSelected: boolean;
    enableOnSelect?: boolean;
    loadingBtn?: boolean;
    isLoading?: boolean;
    hidden?: boolean;
}

interface IStringIndex {
    [key: string]: any;
}

export const CclPersistentDataGrid: React.FC<CclPersistentDataGridProps> = (props) => {
    const initState =
        props.hideToolbar === true
            ? props.initState == null
                ? undefined
                : JSON.parse(props.initState)
            : props.stateSelector?.TableState == null
            ? undefined
            : JSON.parse(props.stateSelector.TableState);

    const emailClaimKey = "email";
    const imKeyClaimKey = "http://schemas.ccl.org/accounts/claims/ccl-identity/id";

    const [saveUserPref, { isLoading: saveIsLoading }] = useSetUserPreferenceMutation();
    const [deleteUserPref, { isLoading: clearIsLoading }] = useRemoveUserPreferenceMutation();
    const apiRef = useGridApiRef();
    const dispatch = useDispatch();
    const claimsSelector = useSelector((state: any) => state.userClaims);
    const [showSaveState, setShowSaveState] = React.useState<boolean>(
        claimsSelector.claims[emailClaimKey] !== "" && claimsSelector.claims[imKeyClaimKey] !== ""
    );
    const [anyRowsSelected, setAnyRowsSelected] = React.useState<boolean>(false);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("info");
    const [columnVisibilityModel, setColumnVisibilityModel] =
        React.useState<GridColumnVisibilityModel>(
            props.columnVisibility
                ? props.columnVisibility
                : initState?.columns?.columnVisibilityModel == null
                ? {
                      id: false,
                      brokerId: false,
                  }
                : initState.columns.columnVisibilityModel
        );

    React.useEffect(() => {
        setShowSaveState(
            claimsSelector.claims[emailClaimKey] !== "" &&
                claimsSelector.claims[imKeyClaimKey] !== ""
        );
    }, [claimsSelector]);

    React.useEffect(() => {
        if (apiRef == null || props.hideToolbar) return;
        const selectedRows = apiRef.current.getSelectedRows();
        apiRef.current.selectRows([...selectedRows.keys()], false, true);
        // eslint-disable-next-line
    }, [props.data]);

    const prevFilterModelFields = React.useRef<Set<string>>(new Set());

    React.useEffect(() => {
        if (apiRef == null || props.hideToolbar || apiRef.current?.setFilterModel == null) return;
        if (props.filterModel == null && prevFilterModelFields.current.size === 0) return;
        const currentItems = apiRef.current.state.filter?.filterModel?.items ?? [];
        const propItems = props.filterModel?.items ?? [];
        const newPropFields = new Set(propItems.map((i) => i.columnField));
        const merged = [
            ...propItems,
            ...currentItems.filter(
                (i: any) =>
                    !newPropFields.has(i.columnField) && !prevFilterModelFields.current.has(i.columnField)
            ),
        ];
        prevFilterModelFields.current = newPropFields;
        const existingModel = apiRef.current.state.filter?.filterModel ?? {};
        apiRef.current.setFilterModel({ ...existingModel, ...props.filterModel, items: merged });
        // eslint-disable-next-line
    }, [props.filterModel]);

    function getDataGridState(): any {
        var st: any = apiRef.current.exportState();
        var fullstate = apiRef.current.state;
        if (st.columns == null) st.columns = { columnVisibilityModel: {} };
        st.columns.columnVisibilityModel = columnVisibilityModel;
        st.density = fullstate.density.value;
        st.preferencePanel = undefined;
        if (prevFilterModelFields.current.size > 0) {
            const exportedItems = st.filter?.filterModel?.items ?? [];
            const stripped = exportedItems.filter(
                (i: any) => !prevFilterModelFields.current.has(i.columnField)
            );
            st.filter = {
                ...st.filter,
                filterModel: { ...st.filter?.filterModel, items: stripped },
            };
        }
        return st;
    }

    const handleTableStateChange = (isClearEvt: boolean, isError: boolean) => {
        //  if (props.onTableStateChange == null) return;

        if (isClearEvt) {
            if (isError) {
                setSnackbarMessage("Clear Settings Failed");
                setSnackbarSeverity("error");
                if (props.onTableStateChange != null)
                    props.onTableStateChange(ePersistentDataGridStateChangeResult.ClearFailure);
            } else {
                setSnackbarMessage("Settings Cleared");
                setSnackbarSeverity("success");
                if (props.onTableStateChange != null)
                    props.onTableStateChange(ePersistentDataGridStateChangeResult.ClearSuccess);
            }
        } else {
            if (isError) {
                setSnackbarMessage("Save Settings Failed");
                setSnackbarSeverity("error");
                if (props.onTableStateChange != null)
                    props.onTableStateChange(ePersistentDataGridStateChangeResult.SaveFailure);
            } else {
                setSnackbarMessage("Settings Saved");
                setSnackbarSeverity("success");
                if (props.onTableStateChange != null)
                    props.onTableStateChange(ePersistentDataGridStateChangeResult.SaveSuccess);
            }
        }
        setShowSnackbar(true);
    };

    const saveStateUserPref = () => {
        saveUserPref({
            email: claimsSelector.claims[emailClaimKey],
            userId: claimsSelector.claims[imKeyClaimKey],
            data: JSON.stringify(getDataGridState()),
            preferenceId: props.userPreferenceId,
        })
            .unwrap()
            .then(() => {
                handleTableStateChange(false, false);
            })
            .catch(() => {
                handleTableStateChange(false, true);
            });
    };

    const deleteStateUserPref = () => {
        deleteUserPref({
            email: claimsSelector.claims[emailClaimKey],
            userId: claimsSelector.claims[imKeyClaimKey],
            data: "", // unused in remove operation
            preferenceId: props.userPreferenceId,
        })
            .unwrap()
            .then(() => {
                handleTableStateChange(true, false);
            })
            .catch((error) => {
                handleTableStateChange(true, true);
            });

        // reset view model
        let visibilitymodel: IStringIndex = {};
        props.columns.map((c) => (visibilitymodel[c.field] = true));
        setColumnVisibilityModel(visibilitymodel);
        // clear filters, preserving any prop-owned filter items
        const propItems = props.filterModel?.items ?? [];
        apiRef.current.setFilterModel({ items: [...propItems] });
        // remove column ordering
        props.columns.map((c, index, arr) => apiRef.current.setColumnIndex(c.field, index));
        // reset density (hard code to 'compact' for now as that's default for access)
        apiRef.current.setDensity("compact");
        // apply init state sort model
        const sm = JSON.parse(props?.initState ?? '{"sorting":{"sortModel":[]}}');
        apiRef.current.setSortModel(sm.sorting.sortModel);
        let st = getDataGridState();
        st.columns.columnVisibilityModel = visibilitymodel;
        dispatch(props.actionCreator(JSON.stringify(st)));
    };

    const saveStateToStore = () => {
        dispatch(props.actionCreator(JSON.stringify(getDataGridState())));
    };

    const onChangeHandler = props.hideToolbar === true ? undefined : saveStateToStore;

    const onColumnViewModelChangeHandler = (newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
        let st = getDataGridState();
        st.columns.columnVisibilityModel = newModel;
        dispatch(props.actionCreator(JSON.stringify(st)));
    };

    const miscButtonClick = (btnid: string) => {
        var btn = props.miscButtons?.find((mb) => mb.id === btnid);
        if (btn == null) return;

        if (!props.hideToolbar && btn.returnSelected) {
            var selectedRowsMap: Map<GridRowId, any> = apiRef.current.getSelectedRows();
            var selectedRows: any[] = [];
            for (let row of Array.from(selectedRowsMap.values())) {
                selectedRows.push(row);
            }
            btn.onClick(selectedRows);
        } else {
            btn.onClick();
        }
    };

    const miscSwitchClick = (btnid: string, checked: boolean) => {
        var btn = props.miscButtons?.find((mb) => mb.id === btnid);
        if (btn?.onToggled == null) return;

        btn.onToggled(checked);
    };

    const onSelectedRowsChange = () => {
        var selectedRowsMap: Map<GridRowId, any> = apiRef.current.getSelectedRows();
        var selectedRows: any[] = [];
        for (let row of Array.from(selectedRowsMap.values())) {
            selectedRows.push(row);
        }
        setAnyRowsSelected(selectedRows.length > 0);

        if (props.onSelectedRowsChange == null) return;

        props.onSelectedRowsChange(selectedRows);
    };

    return (
        <React.Fragment>
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={() => setShowSnackbar(false)}
                />
            ) : null}
            <DataGridPro
                getRowId={(row) => props.getRowIdFn(row)}
                rows={props.data}
                columns={props.columns}
                loading={props.loading}
                components={
                    props.hideToolbar === true ? undefined : { Toolbar: CclDataGridToolbar }
                }
                componentsProps={
                    props.hideToolbar === true
                        ? undefined
                        : {
                              toolbar: {
                                  csvOptions: {
                                      allColumns: true,
                                      fileName: props.exportName
                                          ? props.exportName
                                          : "ccl-file.csv",
                                  },
                                  showSaveState: showSaveState,
                                  savestate: saveStateUserPref,
                                  clearstate: deleteStateUserPref,
                                  resultBar: props.resultBar ?? false,
                                  resultBarText: props.resultBarText ?? "",
                                  miscButtons: props.miscButtons,
                                  btnClick: miscButtonClick,
                                  switchToggled: miscSwitchClick,
                                  hideSettingsButtons: props.hideSettingsButtons,
                                  anySelected: anyRowsSelected,
                                  saveInProgress: saveIsLoading,
                                  clearInProgress: clearIsLoading,
                              },
                          }
                }
                initialState={initState}
                columnVisibilityModel={columnVisibilityModel}
                apiRef={props.hideToolbar === true ? undefined : apiRef}
                hideFooter={props.hideFooter ?? true}
                checkboxSelection={props.showCheckboxes ?? false}
                disableSelectionOnClick={props.disableSelectionOnClick ?? false}
                onColumnOrderChange={onChangeHandler}
                onColumnVisibilityModelChange={(newModel) =>
                    onColumnViewModelChangeHandler(newModel)
                }
                onFilterModelChange={onChangeHandler}
                onPinnedColumnsChange={onChangeHandler}
                onSortModelChange={onChangeHandler}
                onSelectionModelChange={() => onSelectedRowsChange()}
                autoHeight={props.autoHeight ?? undefined}
            />
        </React.Fragment>
    );
};

export default CclPersistentDataGrid;
